import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Box,
  Flex,
  Text,
  HStack,
  VStack,
  Badge,
  Button,
  IconButton,
  Input,
  Heading,
  Card,
  Table,
  DialogRoot,
  DialogBackdrop,
  DialogPositioner,
  DialogContent,
  DialogCloseTrigger,
} from '@chakra-ui/react'
import {
  Circle,
  Clock,
  Video,
  Search,
  ChevronLeft,
  ChevronRight,
  Play,
  Pause,
  Flag,
  Monitor,
  Check,
  X,
  Minus,
  UserX,
  Mail,
  ArrowRight,
  Smartphone,
  Hand,
  FileText,
  Minimize2,
  VideoOff,
  LogIn,
} from 'lucide-react'
import { addHours } from 'date-fns'
import { useMeeting, useVideoRoom } from '../hooks'
import { useResolutions } from '../hooks/useResolutions'
import { createMeetingToken } from '../services/dailyService'
import { DailyVideoCall } from './DailyVideoCall'
import type { Meeting, Resolution } from '../types'
import { MAJORITY_TYPE_LABELS } from '../types'

// Clé API Daily.co
const DAILY_API_KEY = import.meta.env.VITE_DAILY_CO_API_KEY || ''

// État de connexion Daily
interface DailyConnectionState {
  isConnected: boolean
  roomUrl: string | null
  token: string | null
}

// ===== TYPES POUR LE PILOTAGE =====

interface MockParticipant {
  id: string
  ownerId: string
  firstName: string
  lastName: string
  lots: string[]
  tantiemes: number
  status: 'present' | 'represented' | 'vpc' | 'absent' | 'left'
  presenceMode?: 'visio' | 'in_person'
  visioConnected?: boolean
  representedBy?: string
  representedByName?: string
}

interface MockVote {
  participantId: string
  vote: 'for' | 'against' | 'abstain' | null
  source: 'smartphone' | 'manual' | 'vpc' | 'proxy'
  tantiemes: number
}

interface ResolutionVotingState {
  resolutionId: string
  status: 'pending' | 'current' | 'voted' | 'skipped'
  result?: 'adopted' | 'rejected'
  votes: MockVote[]
  forTantiemes: number
  againstTantiemes: number
  abstainTantiemes: number
}

// ===== DONNEES MOCKEES =====

const MOCK_PARTICIPANTS: MockParticipant[] = [
  { id: '1', ownerId: 'o1', firstName: 'Jean', lastName: 'DUPONT', lots: ['12'], tantiemes: 156, status: 'present', presenceMode: 'visio', visioConnected: true },
  { id: '2', ownerId: 'o2', firstName: 'Marie', lastName: 'MARTIN', lots: ['5', '8'], tantiemes: 189, status: 'present', presenceMode: 'in_person' },
  { id: '3', ownerId: 'o3', firstName: 'Paul', lastName: 'DURAND', lots: ['23'], tantiemes: 234, status: 'represented', representedBy: 'o1', representedByName: 'Jean DUPONT' },
  { id: '4', ownerId: 'o4', firstName: 'Sophie', lastName: 'BERNARD', lots: ['7'], tantiemes: 178, status: 'vpc' },
  { id: '5', ownerId: 'o5', firstName: 'Pierre', lastName: 'LEROY', lots: ['15', '16'], tantiemes: 203, status: 'present', presenceMode: 'visio', visioConnected: true },
  { id: '6', ownerId: 'o6', firstName: 'Anne', lastName: 'MOREAU', lots: ['9'], tantiemes: 167, status: 'present', presenceMode: 'in_person' },
  { id: '7', ownerId: 'o7', firstName: 'François', lastName: 'PETIT', lots: ['21'], tantiemes: 143, status: 'absent' },
  { id: '8', ownerId: 'o8', firstName: 'Claire', lastName: 'ROBERT', lots: ['3'], tantiemes: 112, status: 'absent' },
  { id: '9', ownerId: 'o9', firstName: 'Michel', lastName: 'THOMAS', lots: ['18'], tantiemes: 198, status: 'present', presenceMode: 'visio', visioConnected: false },
  { id: '10', ownerId: 'o10', firstName: 'Isabelle', lastName: 'RICHARD', lots: ['11'], tantiemes: 134, status: 'left' },
  { id: '11', ownerId: 'o11', firstName: 'Luc', lastName: 'GARCIA', lots: ['25'], tantiemes: 221, status: 'present', presenceMode: 'in_person' },
  { id: '12', ownerId: 'o12', firstName: 'Emma', lastName: 'MARTINEZ', lots: ['6'], tantiemes: 145, status: 'vpc' },
]

// ===== COMPOSANT PRINCIPAL =====

interface LiveAgPilotingProps {
  meetingId: string
  isOpen: boolean
  onClose: () => void
}

export function LiveAgPiloting({ meetingId, isOpen, onClose }: LiveAgPilotingProps) {
  const { data: meeting, isLoading: meetingLoading } = useMeeting(meetingId)
  const { data: resolutions = [] } = useResolutions(meetingId)
  const { data: videoRoom } = useVideoRoom(meetingId)

  // États du pilotage
  const [startTime] = useState<Date>(new Date())
  const [elapsedTime, setElapsedTime] = useState('0h00')
  const [currentResolutionIndex, setCurrentResolutionIndex] = useState(0)
  const [participants, setParticipants] = useState<MockParticipant[]>(MOCK_PARTICIPANTS)
  const [searchQuery, setSearchQuery] = useState('')
  const [isVisioCollapsed, setIsVisioCollapsed] = useState(false)
  const [isPaused, setIsPaused] = useState(false)

  // États Daily.co
  const [dailyConnection, setDailyConnection] = useState<DailyConnectionState>({
    isConnected: false,
    roomUrl: null,
    token: null,
  })
  const [isJoiningDaily, setIsJoiningDaily] = useState(false)
  const [dailyError, setDailyError] = useState<string | null>(null)

  // États des votes par résolution
  const [resolutionStates, setResolutionStates] = useState<Map<string, ResolutionVotingState>>(new Map())

  // Mise à jour du temps écoulé
  useEffect(() => {
    const interval = setInterval(() => {
      const diff = Date.now() - startTime.getTime()
      const hours = Math.floor(diff / 3600000)
      const minutes = Math.floor((diff % 3600000) / 60000)
      setElapsedTime(`${hours}h${minutes.toString().padStart(2, '0')}`)
    }, 1000)
    return () => clearInterval(interval)
  }, [startTime])

  // Initialiser les états des résolutions
  useEffect(() => {
    if (resolutions.length > 0 && resolutionStates.size === 0) {
      const states = new Map<string, ResolutionVotingState>()
      resolutions.forEach((res, idx) => {
        states.set(res.id, {
          resolutionId: res.id,
          status: idx === 0 ? 'current' : 'pending',
          votes: generateMockVotes(participants, idx === 0),
          forTantiemes: 0,
          againstTantiemes: 0,
          abstainTantiemes: 0,
        })
      })
      setResolutionStates(states)
    }
  }, [resolutions, participants])

  // Générer des votes mockés
  function generateMockVotes(participants: MockParticipant[], withSomeVotes: boolean): MockVote[] {
    return participants
      .filter(p => p.status !== 'absent')
      .map(p => {
        let vote: MockVote['vote'] = null
        let source: MockVote['source'] = 'manual'

        if (p.status === 'vpc') {
          vote = Math.random() > 0.3 ? 'for' : Math.random() > 0.5 ? 'against' : 'abstain'
          source = 'vpc'
        } else if (withSomeVotes && Math.random() > 0.5) {
          vote = Math.random() > 0.4 ? 'for' : Math.random() > 0.5 ? 'against' : 'abstain'
          source = 'smartphone'
        }

        return {
          participantId: p.id,
          vote,
          source,
          tantiemes: p.tantiemes,
        }
      })
  }

  // Calculs des présences
  const presenceStats = useMemo(() => {
    const present = participants.filter(p => p.status === 'present')
    const represented = participants.filter(p => p.status === 'represented')
    const vpc = participants.filter(p => p.status === 'vpc')
    const absent = participants.filter(p => p.status === 'absent')
    const left = participants.filter(p => p.status === 'left')

    const totalTantiemes = participants.reduce((sum, p) => sum + p.tantiemes, 0)
    const presentTantiemes = present.reduce((sum, p) => sum + p.tantiemes, 0)
    const representedTantiemes = represented.reduce((sum, p) => sum + p.tantiemes, 0)
    const vpcTantiemes = vpc.reduce((sum, p) => sum + p.tantiemes, 0)

    return {
      total: participants.length,
      present: present.length,
      represented: represented.length,
      vpc: vpc.length,
      absent: absent.length,
      left: left.length,
      votingCount: present.length + represented.length + vpc.length,
      totalTantiemes,
      presentTantiemes,
      representedTantiemes,
      vpcTantiemes,
      votingTantiemes: presentTantiemes + representedTantiemes + vpcTantiemes,
    }
  }, [participants])

  // Résolution courante
  const currentResolution = resolutions[currentResolutionIndex]
  const currentState = currentResolution ? resolutionStates.get(currentResolution.id) : null

  // Calculs des votes pour la résolution courante
  const voteStats = useMemo(() => {
    if (!currentState) return { for: 0, against: 0, abstain: 0, notVoted: 0, threshold: 0 }

    const votes = currentState.votes
    const forVotes = votes.filter(v => v.vote === 'for').reduce((sum, v) => sum + v.tantiemes, 0)
    const againstVotes = votes.filter(v => v.vote === 'against').reduce((sum, v) => sum + v.tantiemes, 0)
    const abstainVotes = votes.filter(v => v.vote === 'abstain').reduce((sum, v) => sum + v.tantiemes, 0)
    const notVoted = presenceStats.votingTantiemes - forVotes - againstVotes - abstainVotes

    let threshold = 0
    const majorityType = currentResolution?.majority_type || 'simple'
    if (majorityType === 'simple') {
      threshold = Math.floor(presenceStats.votingTantiemes / 2) + 1
    } else if (majorityType === 'absolute' || majorityType === 'absolute_25_1') {
      threshold = Math.floor(presenceStats.totalTantiemes / 2) + 1
    } else if (majorityType === 'double') {
      threshold = Math.floor((presenceStats.totalTantiemes * 2) / 3) + 1
    } else if (majorityType === 'unanimity') {
      threshold = presenceStats.totalTantiemes
    }

    return { for: forVotes, against: againstVotes, abstain: abstainVotes, notVoted, threshold }
  }, [currentState, currentResolution, presenceStats])

  // Navigation
  const goToResolution = (index: number) => {
    if (index >= 0 && index < resolutions.length) {
      if (currentResolution) {
        const oldState = resolutionStates.get(currentResolution.id)
        if (oldState && oldState.status === 'current') {
          setResolutionStates(prev => {
            const newMap = new Map(prev)
            newMap.set(currentResolution.id, { ...oldState, status: 'pending' })
            return newMap
          })
        }
      }

      const newResolution = resolutions[index]
      if (!newResolution) return

      setResolutionStates(prev => {
        const newMap = new Map(prev)
        const state = newMap.get(newResolution.id)
        if (state && state.status !== 'voted') {
          newMap.set(newResolution.id, { ...state, status: 'current' })
        }
        return newMap
      })

      setCurrentResolutionIndex(index)
    }
  }

  // Clôturer le vote
  const closeVote = () => {
    if (!currentResolution || !currentState) return

    const isAdopted = voteStats.for >= voteStats.threshold
    setResolutionStates(prev => {
      const newMap = new Map(prev)
      newMap.set(currentResolution.id, {
        ...currentState,
        status: 'voted',
        result: isAdopted ? 'adopted' : 'rejected',
        forTantiemes: voteStats.for,
        againstTantiemes: voteStats.against,
        abstainTantiemes: voteStats.abstain,
      })
      return newMap
    })
  }

  // Enregistrer un vote
  const recordVote = (participantId: string, vote: 'for' | 'against' | 'abstain') => {
    if (!currentResolution || !currentState) return

    setResolutionStates(prev => {
      const newMap = new Map(prev)
      const votes = currentState.votes.map(v =>
        v.participantId === participantId ? { ...v, vote, source: 'manual' as const } : v
      )
      newMap.set(currentResolution.id, { ...currentState, votes })
      return newMap
    })
  }

  // Marquer présent/absent
  const togglePresence = (participantId: string) => {
    setParticipants(prev => prev.map(p => {
      if (p.id === participantId) {
        if (p.status === 'absent') {
          return { ...p, status: 'present', presenceMode: 'in_person' }
        } else if (p.status === 'present') {
          return { ...p, status: 'left' }
        }
      }
      return p
    }))
  }

  // Filtrer les participants
  const filteredParticipants = useMemo(() => {
    let filtered = participants
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(p =>
        p.firstName.toLowerCase().includes(query) ||
        p.lastName.toLowerCase().includes(query) ||
        p.lots.some(l => l.includes(query))
      )
    }
    const order = { present: 0, represented: 1, vpc: 2, left: 3, absent: 4 }
    return filtered.sort((a, b) => order[a.status] - order[b.status])
  }, [participants, searchQuery])

  // Compteur résolutions traitées
  const treatedCount = useMemo(() => {
    let count = 0
    resolutionStates.forEach(state => {
      if (state.status === 'voted') count++
    })
    return count
  }, [resolutionStates])

  // Rejoindre Daily.co en tant qu'animateur
  const handleJoinAsAnimator = useCallback(async () => {
    if (!videoRoom || !DAILY_API_KEY) {
      setDailyError('Configuration visio manquante')
      return
    }

    setIsJoiningDaily(true)
    setDailyError(null)

    try {
      // Créer un token animateur (isOwner: true)
      const token = await createMeetingToken({
        roomName: videoRoom.daily_room_name,
        userName: 'Animateur AG',
        isOwner: true,
        expiresAt: addHours(new Date(), 6),
        apiKey: DAILY_API_KEY,
      })

      // Stocker la connexion
      setDailyConnection({
        isConnected: true,
        roomUrl: videoRoom.daily_room_url,
        token: token,
      })
    } catch (error) {
      console.error('Erreur connexion Daily.co:', error)
      setDailyError('Impossible de rejoindre la visioconférence')
    } finally {
      setIsJoiningDaily(false)
    }
  }, [videoRoom])

  // Quitter Daily.co
  const handleLeaveDaily = useCallback(() => {
    setDailyConnection({
      isConnected: false,
      roomUrl: null,
      token: null,
    })
  }, [])

  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && onClose()} size="full">
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent
          w="95vw"
          h="95vh"
          maxW="95vw"
          maxH="95vh"
          borderRadius="xl"
          overflow="hidden"
        >
          <DialogCloseTrigger />

          {meetingLoading || !meeting ? (
            <Flex h="full" align="center" justify="center">
              <Text color="gray.500">Chargement...</Text>
            </Flex>
          ) : (
            <Flex direction="column" h="full" overflow="hidden">
              {/* ===== BARRE DE STATUT ===== */}
              <StatusBar
                meeting={meeting}
                elapsedTime={elapsedTime}
                treatedCount={treatedCount}
                totalResolutions={resolutions.length}
                isPaused={isPaused}
                onPause={() => setIsPaused(!isPaused)}
                onClose={onClose}
              />

              {/* ===== CONTENU PRINCIPAL ===== */}
              <Flex flex={1} overflow="hidden">
                {/* Panneau gauche - Présences */}
                <PresencePanel
                  participants={filteredParticipants}
                  stats={presenceStats}
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  onTogglePresence={togglePresence}
                />

                {/* Zone centrale - Résolution & Vote */}
                <CenterPanel
                  resolution={currentResolution}
                  state={currentState}
                  voteStats={voteStats}
                  presenceStats={presenceStats}
                  participants={participants}
                  onRecordVote={recordVote}
                  onCloseVote={closeVote}
                />

                {/* Panneau droit - Visio */}
                {!isVisioCollapsed && (
                  <VisioPanel
                    videoRoom={videoRoom}
                    dailyConnection={dailyConnection}
                    isJoining={isJoiningDaily}
                    error={dailyError}
                    onJoinAsAnimator={handleJoinAsAnimator}
                    onLeave={handleLeaveDaily}
                    onCollapse={() => setIsVisioCollapsed(true)}
                  />
                )}

                {isVisioCollapsed && (
                  <Flex
                    w="40px"
                    bg="gray.50"
                    direction="column"
                    align="center"
                    py={4}
                    borderLeftWidth="1px"
                    borderColor="gray.200"
                  >
                    <IconButton
                      aria-label="Ouvrir visio"
                      size="sm"
                      variant="ghost"
                      onClick={() => setIsVisioCollapsed(false)}
                    >
                      <Video size={16} />
                    </IconButton>
                  </Flex>
                )}
              </Flex>

              {/* ===== BARRE DE NAVIGATION ODJ ===== */}
              <NavigationBar
                resolutions={resolutions}
                resolutionStates={resolutionStates}
                currentIndex={currentResolutionIndex}
                onNavigate={goToResolution}
              />
            </Flex>
          )}
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}

// ===== SOUS-COMPOSANTS =====

// Barre de statut
function StatusBar({
  meeting,
  elapsedTime,
  treatedCount,
  totalResolutions,
  isPaused,
  onPause,
  onClose,
}: {
  meeting: Meeting
  elapsedTime: string
  treatedCount: number
  totalResolutions: number
  isPaused: boolean
  onPause: () => void
  onClose: () => void
}) {
  const [currentTime, setCurrentTime] = useState(new Date())

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000)
    return () => clearInterval(interval)
  }, [])

  return (
    <Flex
      h="56px"
      bg="white"
      px={4}
      align="center"
      justify="space-between"
      borderBottomWidth="1px"
      borderColor="gray.200"
    >
      <HStack gap={4}>
        {/* Indicateur live */}
        <HStack gap={2}>
          <Box
            w={3}
            h={3}
            borderRadius="full"
            bg={isPaused ? 'yellow.400' : 'red.500'}
            animation={isPaused ? undefined : 'pulse 2s infinite'}
            css={{
              '@keyframes pulse': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.5 },
              },
            }}
          />
          <Text fontWeight="bold" fontSize="sm" color={isPaused ? 'yellow.600' : 'red.600'}>
            {isPaused ? 'AG SUSPENDUE' : 'AG EN COURS'}
          </Text>
        </HStack>

        <Text color="gray.300">•</Text>
        <Text fontSize="sm" fontWeight="medium">{meeting.residence?.name || 'Résidence'}</Text>
        <Text color="gray.300">•</Text>
        <HStack gap={1} color="gray.600">
          <Clock size={14} />
          <Text fontSize="sm">{currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</Text>
        </HStack>
        <Text color="gray.300">•</Text>
        <Text fontSize="sm" color="gray.600">Durée: {elapsedTime}</Text>
        <Text color="gray.300">•</Text>
        <Text fontSize="sm" color="gray.600">{treatedCount}/{totalResolutions} résolutions</Text>
      </HStack>

      <HStack gap={2}>
        <Button size="sm" variant="outline" colorPalette="gray">
          <Monitor size={14} />
          Mode Projection
        </Button>
        <Button
          size="sm"
          variant="outline"
          colorPalette={isPaused ? 'green' : 'yellow'}
          onClick={onPause}
        >
          {isPaused ? <Play size={14} /> : <Pause size={14} />}
          {isPaused ? 'Reprendre' : 'Suspendre'}
        </Button>
        <Button size="sm" colorPalette="red" onClick={onClose}>
          <Flag size={14} />
          Clôturer l'AG
        </Button>
      </HStack>
    </Flex>
  )
}

// Panneau présences
function PresencePanel({
  participants,
  stats,
  searchQuery,
  onSearchChange,
  onTogglePresence,
}: {
  participants: MockParticipant[]
  stats: ReturnType<typeof Object>
  searchQuery: string
  onSearchChange: (query: string) => void
  onTogglePresence: (id: string) => void
}) {
  return (
    <Flex
      w="280px"
      direction="column"
      bg="gray.50"
      borderRightWidth="1px"
      borderColor="gray.200"
      overflow="hidden"
    >
      {/* En-tête */}
      <Box p={4} bg="white" borderBottomWidth="1px" borderColor="gray.200">
        <HStack justify="space-between" mb={3}>
          <Heading size="sm" color="gray.700">PRÉSENCES</Heading>
        </HStack>

        {/* Barre visuelle */}
        <HStack gap={0.5} mb={2}>
          {Array.from({ length: 20 }).map((_, i) => (
            <Box
              key={i}
              w={2}
              h={2}
              borderRadius="full"
              bg={i < Math.round((stats.votingCount / stats.total) * 20) ? 'green.400' : 'gray.300'}
            />
          ))}
        </HStack>

        <Text fontSize="sm" color="gray.700" fontWeight="medium">
          {stats.votingCount}/{stats.total} présents ({Math.round((stats.votingCount / stats.total) * 100)}%)
        </Text>
        <Text fontSize="xs" color="gray.500">
          Tantièmes: {stats.votingTantiemes.toLocaleString()}/{stats.totalTantiemes.toLocaleString()}
        </Text>

        {/* Recherche */}
        <Box position="relative" mt={3}>
          <Box position="absolute" left={2} top="50%" transform="translateY(-50%)" color="gray.400">
            <Search size={14} />
          </Box>
          <Input
            size="sm"
            placeholder="Rechercher..."
            pl={8}
            bg="gray.50"
            borderColor="gray.200"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </Box>
      </Box>

      {/* Liste des participants */}
      <VStack flex={1} overflow="auto" p={2} gap={1} align="stretch">
        {participants.map((p) => (
          <ParticipantRow key={p.id} participant={p} onTogglePresence={onTogglePresence} />
        ))}
      </VStack>
    </Flex>
  )
}

// Ligne participant
function ParticipantRow({
  participant: p,
  onTogglePresence,
}: {
  participant: MockParticipant
  onTogglePresence: (id: string) => void
}) {
  const statusIcon = {
    present: <Check size={14} />,
    represented: <ArrowRight size={14} />,
    vpc: <Mail size={14} />,
    absent: <Circle size={14} />,
    left: <UserX size={14} />,
  }

  const statusColor = {
    present: 'green.500',
    represented: 'blue.500',
    vpc: 'purple.500',
    absent: 'gray.400',
    left: 'orange.500',
  }

  return (
    <Box
      p={2}
      borderRadius="md"
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      _hover={{ bg: 'gray.50', borderColor: 'gray.300' }}
      cursor="pointer"
      onClick={() => (p.status === 'absent' || p.status === 'present') && onTogglePresence(p.id)}
    >
      <HStack justify="space-between">
        <HStack gap={2}>
          <Box color={statusColor[p.status]}>{statusIcon[p.status]}</Box>
          <Box>
            <Text fontSize="sm" fontWeight="medium" color="gray.800">
              {p.lastName} {p.firstName}
            </Text>
            <Text fontSize="xs" color="gray.500">
              {p.status === 'represented'
                ? `Représenté par ${p.representedByName}`
                : p.status === 'vpc'
                ? 'Vote par correspondance'
                : p.status === 'left'
                ? 'A quitté la séance'
                : p.status === 'absent'
                ? 'Absent'
                : p.presenceMode === 'visio'
                ? p.visioConnected ? '🎥 Connecté' : '🔴 Déconnecté'
                : '📍 En salle'
              }
              {' • Lot' + (p.lots.length > 1 ? 's' : '')} {p.lots.join(', ')}
            </Text>
          </Box>
        </HStack>
        <Text fontSize="sm" color="gray.500" fontWeight="medium">
          {p.tantiemes}
        </Text>
      </HStack>
    </Box>
  )
}

// Zone centrale
function CenterPanel({
  resolution,
  state,
  voteStats,
  presenceStats,
  participants,
  onRecordVote,
  onCloseVote,
}: {
  resolution: Resolution | undefined
  state: ResolutionVotingState | null | undefined
  voteStats: { for: number; against: number; abstain: number; notVoted: number; threshold: number }
  presenceStats: { votingTantiemes: number; totalTantiemes: number }
  participants: MockParticipant[]
  onRecordVote: (participantId: string, vote: 'for' | 'against' | 'abstain') => void
  onCloseVote: () => void
}) {
  if (!resolution) {
    return (
      <Flex flex={1} align="center" justify="center" direction="column" gap={4} bg="white">
        <Text color="gray.400">Aucune résolution à traiter</Text>
      </Flex>
    )
  }

  const isVoted = state?.status === 'voted'
  const forPercent = presenceStats.votingTantiemes > 0 ? (voteStats.for / presenceStats.votingTantiemes) * 100 : 0
  const againstPercent = presenceStats.votingTantiemes > 0 ? (voteStats.against / presenceStats.votingTantiemes) * 100 : 0
  const abstainPercent = presenceStats.votingTantiemes > 0 ? (voteStats.abstain / presenceStats.votingTantiemes) * 100 : 0
  const thresholdPercent = presenceStats.votingTantiemes > 0 ? (voteStats.threshold / presenceStats.votingTantiemes) * 100 : 50

  return (
    <Flex flex={1} direction="column" overflow="hidden" p={6} gap={4} bg="white">
      {/* En-tête résolution */}
      <Box flexShrink={0}>
        <HStack justify="space-between" mb={2}>
          <Heading size="lg" color="gray.800">RÉSOLUTION N°{resolution.order_number}</Heading>
          <Button size="sm" variant="outline" colorPalette="gray">
            Sauter ▶
          </Button>
        </HStack>

        <Card.Root variant="outline">
          <Card.Body>
            <Text fontWeight="bold" fontSize="lg" mb={2} color="gray.800">{resolution.title}</Text>
            <Text fontSize="sm" color="gray.600" lineClamp={3}>
              {resolution.description || 'Pas de contenu détaillé'}
            </Text>
          </Card.Body>
        </Card.Root>

        <HStack mt={3} gap={4} flexWrap="wrap">
          <Badge colorPalette="blue" variant="subtle">
            {MAJORITY_TYPE_LABELS[resolution.majority_type as keyof typeof MAJORITY_TYPE_LABELS] || resolution.majority_type}
          </Badge>
          {resolution.resolution_category && (
            <Text fontSize="sm" color="gray.500">
              Catégorie: {resolution.resolution_category}
            </Text>
          )}
        </HStack>
      </Box>

      {/* Interface de vote */}
      <Box flex={1} overflowY="auto" overflowX="hidden" css={{ scrollbarGutter: 'stable' }}>
        {isVoted ? (
          <VoteResult state={state!} voteStats={voteStats} />
        ) : (
          <VStack gap={4} align="stretch">
            <Box textAlign="center">
              <Text fontSize="xl" fontWeight="bold" color="gray.700" mb={4}>VOTE EN COURS</Text>
            </Box>

            {/* Barre de progression */}
            <Box bg="gray.50" p={4} borderRadius="lg" position="relative" borderWidth="1px" borderColor="gray.200">
              <Flex h={8} bg="gray.200" borderRadius="md" overflow="hidden">
                <Box w={`${forPercent}%`} bg="green.500" transition="width 0.3s" />
                <Box w={`${againstPercent}%`} bg="red.500" transition="width 0.3s" />
                <Box w={`${abstainPercent}%`} bg="gray.400" transition="width 0.3s" />
              </Flex>

              {/* Marqueur seuil */}
              <Box
                position="absolute"
                left={`calc(${Math.min(thresholdPercent, 100)}% + 16px)`}
                top={4}
                bottom={4}
                w="2px"
                bg="gray.800"
              />

              <HStack justify="space-around" mt={4}>
                <VStack gap={0}>
                  <Text fontSize="2xl" fontWeight="bold" color="green.600">{voteStats.for}</Text>
                  <Text fontSize="sm" color="gray.500">POUR ({forPercent.toFixed(1)}%)</Text>
                </VStack>
                <VStack gap={0}>
                  <Text fontSize="2xl" fontWeight="bold" color="red.600">{voteStats.against}</Text>
                  <Text fontSize="sm" color="gray.500">CONTRE ({againstPercent.toFixed(1)}%)</Text>
                </VStack>
                <VStack gap={0}>
                  <Text fontSize="2xl" fontWeight="bold" color="gray.500">{voteStats.abstain}</Text>
                  <Text fontSize="sm" color="gray.500">ABST. ({abstainPercent.toFixed(1)}%)</Text>
                </VStack>
              </HStack>

              <Text textAlign="center" mt={3} fontSize="sm" color="gray.500">
                Seuil requis: {voteStats.threshold} voix (50%+1) • N'ont pas voté: {voteStats.notVoted} tantièmes
              </Text>
            </Box>

            {/* Tableau de vote */}
            <VoteTable
              participants={participants}
              votes={state?.votes || []}
              onRecordVote={onRecordVote}
            />

            {/* Actions */}
            <HStack justify="center" gap={4}>
              <Button colorPalette="green" size="lg" onClick={onCloseVote}>
                Clôturer le vote
              </Button>
              <Button variant="outline" colorPalette="red" size="lg">
                Annuler le vote
              </Button>
            </HStack>
          </VStack>
        )}
      </Box>
    </Flex>
  )
}

// Résultat du vote
function VoteResult({
  state,
  voteStats,
}: {
  state: ResolutionVotingState
  voteStats: { for: number; against: number; abstain: number; threshold: number }
}) {
  const isAdopted = state.result === 'adopted'

  return (
    <VStack gap={6} py={8}>
      <Box
        p={8}
        borderRadius="xl"
        bg={isAdopted ? 'green.50' : 'red.50'}
        borderWidth="2px"
        borderColor={isAdopted ? 'green.200' : 'red.200'}
        textAlign="center"
      >
        <Text fontSize="4xl" fontWeight="bold" color={isAdopted ? 'green.600' : 'red.600'}>
          {isAdopted ? '✓ ADOPTÉE' : '✗ REJETÉE'}
        </Text>
        <Text fontSize="xl" color="gray.600" mt={2}>
          {voteStats.for} voix POUR / {voteStats.threshold} requises
        </Text>
      </Box>

      <HStack gap={4}>
        <Badge colorPalette="green" p={3} fontSize="md">
          POUR: {voteStats.for}
        </Badge>
        <Badge colorPalette="red" p={3} fontSize="md">
          CONTRE: {voteStats.against}
        </Badge>
        <Badge colorPalette="gray" p={3} fontSize="md">
          ABST: {voteStats.abstain}
        </Badge>
      </HStack>

      <Button colorPalette="brand" size="lg">
        Résolution suivante →
      </Button>
    </VStack>
  )
}

// Tableau de vote
function VoteTable({
  participants,
  votes,
  onRecordVote,
}: {
  participants: MockParticipant[]
  votes: MockVote[]
  onRecordVote: (participantId: string, vote: 'for' | 'against' | 'abstain') => void
}) {
  const votingParticipants = participants.filter(p => p.status !== 'absent')

  return (
    <Card.Root variant="outline">
      <Table.Root size="sm">
        <Table.Header>
          <Table.Row bg="gray.50">
            <Table.ColumnHeader>Copropriétaire</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="right">Tantièmes</Table.ColumnHeader>
            <Table.ColumnHeader textAlign="center">Vote</Table.ColumnHeader>
            <Table.ColumnHeader>Source</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {votingParticipants.slice(0, 8).map((p) => {
            const vote = votes.find(v => v.participantId === p.id)
            const isVpc = p.status === 'vpc'

            return (
              <Table.Row key={p.id} _hover={{ bg: 'gray.50' }}>
                <Table.Cell>
                  <Text fontSize="sm">{p.lastName} {p.firstName}</Text>
                  {p.status === 'represented' && (
                    <Text fontSize="xs" color="gray.500">
                      (représenté par {p.representedByName})
                    </Text>
                  )}
                </Table.Cell>
                <Table.Cell textAlign="right">{p.tantiemes}</Table.Cell>
                <Table.Cell textAlign="center">
                  {vote?.vote ? (
                    <Badge
                      colorPalette={vote.vote === 'for' ? 'green' : vote.vote === 'against' ? 'red' : 'gray'}
                      cursor={isVpc ? 'default' : 'pointer'}
                    >
                      {vote.vote === 'for' ? 'POUR' : vote.vote === 'against' ? 'CONTRE' : 'ABST.'}
                    </Badge>
                  ) : (
                    <HStack gap={1} justify="center">
                      <IconButton
                        aria-label="Pour"
                        size="xs"
                        colorPalette="green"
                        variant="outline"
                        onClick={() => onRecordVote(p.id, 'for')}
                      >
                        <Check size={12} />
                      </IconButton>
                      <IconButton
                        aria-label="Contre"
                        size="xs"
                        colorPalette="red"
                        variant="outline"
                        onClick={() => onRecordVote(p.id, 'against')}
                      >
                        <X size={12} />
                      </IconButton>
                      <IconButton
                        aria-label="Abstention"
                        size="xs"
                        colorPalette="gray"
                        variant="outline"
                        onClick={() => onRecordVote(p.id, 'abstain')}
                      >
                        <Minus size={12} />
                      </IconButton>
                    </HStack>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <HStack gap={1}>
                    {vote?.source === 'smartphone' && <Smartphone size={14} />}
                    {vote?.source === 'vpc' && <Mail size={14} />}
                    {vote?.source === 'manual' && <Hand size={14} />}
                    {vote?.source === 'proxy' && <FileText size={14} />}
                    {!vote?.vote && <Clock size={14} className="text-gray-400" />}
                    <Text fontSize="xs" color="gray.500">
                      {vote?.source === 'smartphone' && 'Smartphone'}
                      {vote?.source === 'vpc' && 'VPC'}
                      {vote?.source === 'manual' && 'Manuel'}
                      {vote?.source === 'proxy' && 'Pouvoir'}
                      {!vote?.vote && 'En attente'}
                    </Text>
                  </HStack>
                </Table.Cell>
              </Table.Row>
            )
          })}
        </Table.Body>
      </Table.Root>
    </Card.Root>
  )
}

// Panneau visio avec intégration Daily.co SDK React
function VisioPanel({
  videoRoom,
  dailyConnection,
  isJoining,
  error,
  onJoinAsAnimator,
  onLeave,
  onCollapse,
}: {
  videoRoom: import('../services/dailyService').VideoRoom | null | undefined
  dailyConnection: DailyConnectionState
  isJoining: boolean
  error: string | null
  onJoinAsAnimator: () => void
  onLeave: () => void
  onCollapse: () => void
}) {
  const hasVideoRoom = !!videoRoom
  const isConnected = dailyConnection.isConnected && dailyConnection.roomUrl && dailyConnection.token

  return (
    <Flex
      w="350px"
      direction="column"
      bg="white"
      borderLeftWidth="1px"
      borderColor="gray.200"
    >
      {/* En-tête */}
      <HStack p={3} justify="space-between" borderBottomWidth="1px" borderColor="gray.200" flexShrink={0}>
        <HStack gap={2}>
          <Heading size="sm" color="gray.700">VISIO</Heading>
          {isConnected && (
            <Badge colorPalette="green" size="sm">Connecté</Badge>
          )}
        </HStack>
        <HStack gap={1}>
          <IconButton aria-label="Réduire" size="xs" variant="ghost" onClick={onCollapse}>
            <Minimize2 size={14} />
          </IconButton>
        </HStack>
      </HStack>

      {/* Zone vidéo principale */}
      <Box flex={1} bg="gray.900" position="relative" minH="300px">
        {!hasVideoRoom ? (
          // Pas de room vidéo configurée
          <Flex
            position="absolute"
            inset={0}
            align="center"
            justify="center"
            direction="column"
            gap={3}
            p={4}
          >
            <VideoOff size={48} color="#6B7280" />
            <Text color="gray.400" fontSize="sm" textAlign="center">
              Visioconférence non activée pour cette AG
            </Text>
          </Flex>
        ) : !isConnected ? (
          // Room existe mais pas connecté
          <Flex
            position="absolute"
            inset={0}
            align="center"
            justify="center"
            direction="column"
            gap={4}
            p={4}
          >
            <Video size={48} color="#9CA3AF" />
            <Text color="gray.300" fontSize="sm" textAlign="center">
              Rejoignez la visioconférence en tant qu'animateur de l'AG
            </Text>
            {error && (
              <Text color="red.400" fontSize="xs" textAlign="center">
                {error}
              </Text>
            )}
            <Button
              colorPalette="brand"
              size="md"
              onClick={onJoinAsAnimator}
              loading={isJoining}
              loadingText="Connexion..."
            >
              <LogIn size={16} />
              Rejoindre en tant qu'animateur
            </Button>
            {videoRoom && (
              <Text fontSize="xs" color="gray.500" mt={2}>
                Room: {videoRoom.daily_room_name}
              </Text>
            )}
          </Flex>
        ) : (
          // Connecté - afficher le composant DailyVideoCall
          <DailyVideoCall
            roomUrl={dailyConnection.roomUrl!}
            token={dailyConnection.token!}
            userName="Animateur AG"
            onLeave={onLeave}
            onError={(err) => console.error('Daily error:', err)}
          />
        )}
      </Box>

      {/* Infos room (si pas connecté mais room existe) */}
      {!isConnected && hasVideoRoom && (
        <Box p={3} borderTopWidth="1px" borderColor="gray.200" flexShrink={0}>
          <VStack gap={1} align="stretch">
            <Text fontSize="xs" color="gray.500">
              Statut: {videoRoom?.status === 'pending' ? 'En attente' : videoRoom?.status === 'active' ? 'Active' : videoRoom?.status}
            </Text>
          </VStack>
        </Box>
      )}
    </Flex>
  )
}

// Barre de navigation
function NavigationBar({
  resolutions,
  resolutionStates,
  currentIndex,
  onNavigate,
}: {
  resolutions: Resolution[]
  resolutionStates: Map<string, ResolutionVotingState>
  currentIndex: number
  onNavigate: (index: number) => void
}) {
  return (
    <Flex
      h="64px"
      bg="white"
      px={4}
      align="center"
      justify="center"
      borderTopWidth="1px"
      borderColor="gray.200"
      gap={4}
    >
      <Button
        size="sm"
        variant="ghost"
        colorPalette="gray"
        disabled={currentIndex === 0}
        onClick={() => onNavigate(currentIndex - 1)}
      >
        <ChevronLeft size={16} />
        Préc.
      </Button>

      <HStack gap={2} overflowX="auto" maxW="70%">
        {resolutions.map((res, idx) => {
          const state = resolutionStates.get(res.id)
          const isCurrent = idx === currentIndex

          let bg = 'gray.200'
          let borderColor = 'transparent'
          let textColor = 'gray.600'

          if (state?.status === 'voted') {
            bg = state.result === 'adopted' ? 'green.500' : 'red.500'
            textColor = 'white'
          } else if (isCurrent) {
            bg = 'brand.500'
            borderColor = 'brand.300'
            textColor = 'white'
          } else if (state?.status === 'skipped') {
            bg = 'gray.300'
          }

          return (
            <Box
              key={res.id}
              w={8}
              h={8}
              borderRadius="full"
              bg={bg}
              display="flex"
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              borderWidth={2}
              borderColor={borderColor}
              _hover={{ transform: 'scale(1.1)' }}
              transition="all 0.2s"
              onClick={() => onNavigate(idx)}
              title={res.title}
            >
              <Text fontSize="xs" fontWeight="bold" color={textColor}>
                {idx + 1}
              </Text>
            </Box>
          )
        })}
      </HStack>

      <Button
        size="sm"
        variant="ghost"
        colorPalette="gray"
        disabled={currentIndex === resolutions.length - 1}
        onClick={() => onNavigate(currentIndex + 1)}
      >
        Suiv.
        <ChevronRight size={16} />
      </Button>
    </Flex>
  )
}

export default LiveAgPiloting
